import React, {
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import UbiquitiHostContext from '@totem/components/ubiquiti/hosts/List/ubiquitiHostContainerContext';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { Params } from '@totem/types/common';
import { isNotNull } from '@totem/utilities/common';
import { UBIQUITI_HOSTS_ENDPOINT } from '@totem/utilities/endpoints';

import { IBDHostResults } from '../../types';

type Props = {
  refresh?: boolean;
  onRecordTotalChanged?: (total: number) => void;
  onDataRefreshRequested?: () => void;
  children?: ReactNode;
};

const UbiquitiHostsContainer = ({
  refresh,
  onRecordTotalChanged,
  onDataRefreshRequested,
  children,
}: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const {
    buildParameters,
    filterUpdateCount,
    sortingUpdateCount,
    pagingUpdateCount,
  } = useContext(InputContext);

  const [refreshData, setRefreshData] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [hostData, setHostData] = useState<IBDHostResults>(null);
  const fetchAbortController = useRef<AbortController | null>(null);

  useEffect(() => {
    if (isNotNull(onRecordTotalChanged)) {
      onRecordTotalChanged(totalRecords);
    }
  }, [onRecordTotalChanged, totalRecords]);

  useEffect(() => {
    if (refresh && isNotNull(onDataRefreshRequested)) {
      setRefreshData(true);

      onDataRefreshRequested();
    }
  }, [refresh, onDataRefreshRequested]);

  useEffect(() => {
    setRefreshData(true);
  }, [
    userProfile.organization.id,
    filterUpdateCount,
    pagingUpdateCount,
    sortingUpdateCount,
  ]);

  useEffect(() => {
    if (refreshData) {
      fetchAbortController.current?.abort();

      const controller = new AbortController();

      fetchAbortController.current = controller;

      setRefreshData(false);

      setIsLoading(true);
      const params: Params = buildParameters();

      fetch(`${UBIQUITI_HOSTS_ENDPOINT}`, {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(params),
        signal: controller.signal,
      })
        .then((res) => res.json())
        .then((result: IBDHostResults) => {
          setHostData(result);

          setTotalRecords(result.paging.totalRecords);
        })
        .then(() => {
          setIsLoading(false);
        })
        .catch((err: Error) => {
          if (err.name !== 'AbortError') {
            setIsLoading(false);
          }
        });
    }
  }, [refreshData, refresh, getApiToken]);

  return (
    <UbiquitiHostContext.Provider
      value={{
        loading: isLoading,
        hostData,
        totalRecords,
      }}
    >
      <div>{children}</div>
    </UbiquitiHostContext.Provider>
  );
};

export default UbiquitiHostsContainer;
